import React, { useEffect, useState } from 'react';
import { ReactComponent as RefreshIcon } from '../../Icons/refresh-icon.svg';
import MasteryBrainIcon from '../Achievements/MasteryBrainIcon';
import { loadSnippetsMasteredFromCacheOnly } from '../../services/profileAchievementStatsService';
import {
  SNIPPET_MASTERY_TOOLTIP,
  formatSnippetsMasteredProfileAria,
} from '../../constants/snippetMasteryCopy';
import { subscribeSnippetMasteryCountChanged } from '../../utils/snippetMasteryCountEvents';
import './DailyGoalHeader.css';

const DailyGoalHeader = ({ userId, hideForGuest, dueItemsCount = 0, onReviewClick }) => {
  const [snippetsMasteredCount, setSnippetsMasteredCount] = useState(0);
  const [snippetsMasteredLoading, setSnippetsMasteredLoading] = useState(false);

  useEffect(() => {
    if (!userId || hideForGuest) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setSnippetsMasteredLoading(true);
      try {
        const count = await loadSnippetsMasteredFromCacheOnly(userId, '');
        if (!cancelled) setSnippetsMasteredCount(count ?? 0);
      } catch (error) {
        console.error('DailyGoalHeader: failed to load mastered snippet count', error);
        if (!cancelled) setSnippetsMasteredCount(0);
      } finally {
        if (!cancelled) setSnippetsMasteredLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, hideForGuest]);

  useEffect(() => {
    if (!userId || hideForGuest) {
      return undefined;
    }

    return subscribeSnippetMasteryCountChanged((delta) => {
      setSnippetsMasteredCount((prev) => Math.max(0, prev + delta));
      setSnippetsMasteredLoading(false);
    });
  }, [userId, hideForGuest]);

  if (hideForGuest) {
    return null;
  }

  return (
    <div className="header-progress-group">
      <button
        type="button"
        className="review-button header-progress-group__review-btn"
        onClick={onReviewClick}
        aria-label={
          dueItemsCount > 0
            ? `Review queue: ${dueItemsCount} due`
            : 'Open review queue'
        }
        title={dueItemsCount > 0 ? `${dueItemsCount} due for review` : 'Review queue'}
      >
        <RefreshIcon aria-hidden />
        {dueItemsCount > 0 ? (
          <span className="header-progress-group__review-badge" aria-hidden>
            {dueItemsCount}
          </span>
        ) : null}
      </button>

      <span className="header-progress-group__divider" aria-hidden />

      <div
        className="header-progress-group__mastery"
        title={SNIPPET_MASTERY_TOOLTIP}
        aria-label={
          snippetsMasteredLoading
            ? 'Snippets mastered, loading'
            : formatSnippetsMasteredProfileAria(snippetsMasteredCount)
        }
      >
        <MasteryBrainIcon className="header-progress-group__mastery-icon" />
        <span className="header-progress-group__mastery-value">
          {snippetsMasteredLoading ? '–' : snippetsMasteredCount}
        </span>
      </div>
    </div>
  );
};

export default DailyGoalHeader;
